import React, { useEffect, useState } from "react"
import { View, TouchableOpacity } from "react-native"
import Slider from "@react-native-community/slider"
import { BFColors } from "@/constants/Colors"
import { type CustomCompOptionProps } from "@/utils/CustomizeCompsOptions"
import BFText from "@/components/standard/BFText"
import CustomCompAdjustmentView from "@/components/customizecomps/CustomCompAdjustmentView"
import {
  type CustomMeasurementUnit,
  formatCustomUnit,
} from "@/utils/CustomizedConversions"

interface CustomCompSliderProps {
  title: string
  value: string | number | null
  options: CustomCompOptionProps
  onValueChanged: (val: string | number | null) => void
  bottomDescription?: string
  measurementUnit?: CustomMeasurementUnit
}

const CustomCompSlider: React.FC<CustomCompSliderProps> = ({
  title,
  value,
  options,
  onValueChanged,
  bottomDescription = "Miles away",
  measurementUnit,
}: CustomCompSliderProps) => {
  const [sliderValue, setSliderValue] = useState<number>(0)

  useEffect(() => {
    const selectedIdx = findClosestIndex(value, options.options)
    setSliderValue(selectedIdx / (options.options.length - 1))
  }, [value, options.options])

  const findClosestIndex = (
    value: string | number | null,
    options: Array<{ name: string | null; value: string | number | null }>
  ) => {
    if (value === null) return 0
    let closestIdx = 0
    let smallestDiff = Infinity
    options.forEach((option, idx) => {
      const diff = Math.abs(Number(option.value) - Number(value))
      if (diff < smallestDiff) {
        smallestDiff = diff
        closestIdx = idx
      }
    })
    return closestIdx
  }

  const onSliderChanged = (val: number) => {
    const idx = Math.round(val * (options.options.length - 1))
    const newValue = options.options[idx]?.value
    if (newValue !== value) {
      onValueChanged(newValue)
    }
    setSliderValue(val)
  }

  const selectedIdx = findClosestIndex(value, options.options)
  const selectedOption = formatCustomUnit(
    (options.options[selectedIdx]?.value as number) || 0,
    measurementUnit
  )

  return (
    <CustomCompAdjustmentView
      title={title}
      label={`${options.prefix} ${selectedOption} ${options.suffix}`}
    >
      <View className="flex flex-1 mb-2 items-center w-full px-5">
        <View className="flex flex-1 pt-6 w-full">
          {/* TODO: Implement on Range component...
           <BFText
            className={`absolute text-calm-blue text-[12px] items-center self-center w-[26px] font-spacegrotesk top-1`}
            style={{
              left: 340 * sliderValue - 30 * ((340 * sliderValue) / 340),
            }}
          >
            {selectedOption}
          </BFText> */}

          <Slider
            style={{ alignItems: "center" }}
            thumbImage={require("@/assets/slider-thumb.png")}
            minimumValue={0}
            maximumValue={1}
            value={sliderValue}
            minimumTrackTintColor={BFColors.calmBlue}
            maximumTrackTintColor={BFColors.lightGrey}
            onValueChange={onSliderChanged}
          />
          <View className="flex-row justify-between w-full mt-4">
            {options.options.map((option, index) => (
              <TouchableOpacity
                testID={`opion-${option.value}`}
                key={index}
                onPress={() => {
                  onValueChanged(option.value)
                }}
                className="flex-1 items-center"
                hitSlop={8}
              >
                <BFText className="text-calm-blue text-xs font-spacegrotesk">
                  {option.name}
                </BFText>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
      <BFText className="flex-1 self-center my-3 font-spacegrotesk leading-none tracking-tight text-gray-800 text-[11px]">
        {bottomDescription}
      </BFText>
    </CustomCompAdjustmentView>
  )
}

export default CustomCompSlider
